import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, Shield, Headphones, CheckCircle, 
  ChevronDown, Calendar, Users, MapPin, 
  Phone, Mail, User, Building, Globe
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { bookingTranslations } from '../translations/booking';
import { useManagedContent } from '../hooks/useManagedContent';
import '../App.css';

function Booking() {
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'booking',
    language,
    defaultTranslations: bookingTranslations
  });
  const [formData, setFormData] = useState({
    // Contact Information
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    country: '',
    
    // Training Camp Details
    sport: '',
    sportOther: '',
    location: '',
    numAthletes: '',
    numCoaches: '',
    startDate: '',
    endDate: '',
    
    // Package & Services
    packageType: '',
    services: [],
    accommodationType: '',
    mealPreferences: [],
    foodAllergies: '',
    
    // Additional Information
    specialRequirements: '',
    hearAboutUs: '',
    budgetRange: '',
    
    // Consent
    privacyConsent: false,
    marketingConsent: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [duration, setDuration] = useState({ nights: 0, days: 0 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end > start) {
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDuration({ nights: diffDays - 1, days: diffDays });
      } else {
        setDuration({ nights: 0, days: 0 });
      }
    }
  }, [formData.startDate, formData.endDate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'services' || name === 'mealPreferences') {
        setFormData(prev => ({
          ...prev,
          [name]: checked 
            ? [...prev[name], value]
            : prev[name].filter(item => item !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (t) => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = t.errors.fullNameRequired;
    if (!formData.email.trim()) {
      newErrors.email = t.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.errors.emailInvalid;
    }
    if (!formData.phone.trim()) newErrors.phone = t.errors.phoneRequired;
    if (!formData.sport) newErrors.sport = t.errors.sportRequired;
    if (!formData.location) newErrors.location = t.errors.locationRequired;
    if (!formData.numAthletes || parseInt(formData.numAthletes) < 1) {
      newErrors.numAthletes = t.errors.numAthletesRequired;
    }
    if (!formData.startDate) newErrors.startDate = t.errors.startDateRequired;
    if (!formData.endDate) newErrors.endDate = t.errors.endDateRequired;
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) newErrors.endDate = t.errors.endDateAfterStart;
    }
    if (!formData.privacyConsent) newErrors.privacyConsent = t.errors.privacyRequired;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm(t)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      // Scroll to top when showing success page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // In production, send data to backend API
      console.log('Form submitted:', formData);
    }, 1500);
  };

  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSubmitted]);

  if (isSubmitted) {
    return (
      <div className="App">
        <Header />
        <section className="booking-success-section">
          <div className="container">
            <motion.div
              className="success-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <CheckCircle size={64} color="#10B981" />
              <h1>{t.success.title}</h1>
              <p>{t.success.message}</p>
              <Link to="/" className="btn-primary">
                {t.success.returnHome}
              </Link>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />

      {/* Hero Section */}
      <section className="booking-hero-section">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">{t.breadcrumb.home}</Link>
            <span> / </span>
            <span>{t.breadcrumb.bookNow}</span>
          </nav>
          
          <motion.div
            className="booking-hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>{t.hero.title}</h1>
            <p className="hero-subheading">{t.hero.subheading}</p>
            <p className="hero-description">{t.hero.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="booking-form-section">
        <div className="container">
          <form className="booking-form" onSubmit={handleSubmit}>
            {/* Step 1: Contact Information */}
            <div className="form-section">
              <h2 className="form-section-heading">
                <User size={24} />
                {t.sections.contact}
              </h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">
                    {t.labels.fullName} <span className="required">{t.required}</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder={t.placeholders.fullName}
                    className={errors.fullName ? 'error' : ''}
                    required
                  />
                  {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    {t.labels.email} <span className="required">{t.required}</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.placeholders.email}
                    className={errors.email ? 'error' : ''}
                    required
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">
                    {t.labels.phone} <span className="required">{t.required}</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.placeholders.phone}
                    className={errors.phone ? 'error' : ''}
                    required
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="organization">{t.labels.organization}</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder={t.placeholders.organization}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="country">{t.labels.country}</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  {t.countryOptions.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Step 2: Training Camp Details */}
            <div className="form-section">
              <h2 className="form-section-heading">
                <Calendar size={24} />
                {t.sections.trainingCamp}
              </h2>

              <div className="form-group">
                <label htmlFor="sport">
                  {t.labels.sport} <span className="required">{t.required}</span>
                </label>
                <select
                  id="sport"
                  name="sport"
                  value={formData.sport}
                  onChange={handleChange}
                  className={errors.sport ? 'error' : ''}
                  required
                >
                  {t.sportOptions.map((opt) => (
                    <option key={opt.value || 'sport-empty'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.sport && <span className="error-message">{errors.sport}</span>}
                
                {formData.sport === 'other' && (
                  <input
                    type="text"
                    name="sportOther"
                    value={formData.sportOther}
                    onChange={handleChange}
                    placeholder={t.placeholders.sportOther}
                    className="form-group-margin-top"
                  />
                )}
              </div>

              <div className="form-group">
                <label htmlFor="location">
                  {t.labels.location} <span className="required">{t.required}</span>
                </label>
                <select
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className={errors.location ? 'error' : ''}
                  required
                >
                  {t.locationOptions.map((opt) => (
                    <option key={opt.value || 'loc-empty'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.location && <span className="error-message">{errors.location}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="numAthletes">
                    {t.labels.numAthletes} <span className="required">{t.required}</span>
                  </label>
                  <input
                    type="number"
                    id="numAthletes"
                    name="numAthletes"
                    value={formData.numAthletes}
                    onChange={handleChange}
                    placeholder={t.placeholders.numAthletes}
                    min="1"
                    className={errors.numAthletes ? 'error' : ''}
                    required
                  />
                  {errors.numAthletes && <span className="error-message">{errors.numAthletes}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="numCoaches">{t.labels.numCoaches}</label>
                  <input
                    type="number"
                    id="numCoaches"
                    name="numCoaches"
                    value={formData.numCoaches}
                    onChange={handleChange}
                    placeholder={t.placeholders.numCoaches}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startDate">
                    {t.labels.startDate} <span className="required">{t.required}</span>
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className={errors.startDate ? 'error' : ''}
                    required
                  />
                  {errors.startDate && <span className="error-message">{errors.startDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">
                    {t.labels.endDate} <span className="required">{t.required}</span>
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    min={formData.startDate || ''}
                    className={errors.endDate ? 'error' : ''}
                    required
                  />
                  {errors.endDate && <span className="error-message">{errors.endDate}</span>}
                </div>
              </div>

              {duration.days > 0 && (
                <div className="duration-display">
                  <strong>{t.duration}</strong> {duration.nights} {t.nights} / {duration.days} {t.days}
                </div>
              )}
            </div>

            {/* Step 3: Package & Services */}
            <div className="form-section">
              <h2 className="form-section-heading">
                <Building size={24} />
                {t.sections.packageServices}
              </h2>

              <div className="form-group">
                <label>{t.labels.packageType}</label>
                <div className="radio-group">
                  {t.packageTypes.map((p) => (
                    <label key={p.value} className="radio-label">
                      <input
                        type="radio"
                        name="packageType"
                        value={p.value}
                        checked={formData.packageType === p.value}
                        onChange={handleChange}
                      />
                      <div>
                        <strong>{p.title}</strong>
                        <p>{p.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{t.labels.servicesNeeded}</label>
                <div className="checkbox-group">
                  {t.servicesList.map((item) => (
                    <label key={item.value} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="services"
                        value={item.value}
                        checked={formData.services.includes(item.value)}
                        onChange={handleChange}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.services.includes('accommodation') && (
                <div className="form-group">
                  <label>{t.labels.accommodationType}</label>
                  <div className="radio-group">
                    {t.accommodationTypes.map((a) => (
                      <label key={a.value} className="radio-label">
                        <input
                          type="radio"
                          name="accommodationType"
                          value={a.value}
                          checked={formData.accommodationType === a.value}
                          onChange={handleChange}
                        />
                        <span>{a.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.services.includes('meals-(breakfast,-lunch,-dinner)') && (
                <div className="form-group">
                  <label>{t.labels.mealPreferences}</label>
                  <div className="checkbox-group">
                    {t.mealPrefsList.map((m) => (
                      <label key={m.value} className="checkbox-label">
                        <input
                          type="checkbox"
                          name="mealPreferences"
                          value={m.value}
                          checked={formData.mealPreferences.includes(m.value)}
                          onChange={handleChange}
                        />
                        <span>{m.label}</span>
                      </label>
                    ))}
                  </div>
                  {formData.mealPreferences.includes('food-allergies') && (
                    <textarea
                      name="foodAllergies"
                      value={formData.foodAllergies}
                      onChange={handleChange}
                      placeholder={t.placeholders.foodAllergies}
                      rows="3"
                      className="form-group-margin-top"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Step 4: Additional Information */}
            <div className="form-section">
              <h2 className="form-section-heading">
                <Users size={24} />
                {t.sections.additional}
              </h2>

              <div className="form-group">
                <label htmlFor="specialRequirements">{t.labels.specialRequirements}</label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleChange}
                  placeholder={t.placeholders.specialRequirements}
                  rows="6"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="hearAboutUs">{t.labels.hearAboutUs}</label>
                  <select
                    id="hearAboutUs"
                    name="hearAboutUs"
                    value={formData.hearAboutUs}
                    onChange={handleChange}
                  >
                    {t.hearAboutUsOptions.map((opt) => (
                      <option key={opt.value || 'hear-empty'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="budgetRange">{t.labels.budgetRange}</label>
                  <select
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                  >
                    {t.budgetOptions.map((opt) => (
                      <option key={opt.value || 'budget-empty'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Step 5: Consent & Submit */}
            <div className="form-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="privacyConsent"
                    checked={formData.privacyConsent}
                    onChange={handleChange}
                    required
                  />
                  <span>
                    {t.consent.privacy}{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      {t.consent.privacyLink}
                    </a>{' '}
                    {t.consent.and}{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      {t.consent.termsLink}
                    </a>
                    <span className="required"> {t.required}</span>
                  </span>
                </label>
                {errors.privacyConsent && (
                  <span className="error-message">{errors.privacyConsent}</span>
                )}
                <p className="help-text">{t.consent.helpText}</p>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={handleChange}
                  />
                  <span>{t.consent.marketing}</span>
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t.submitting : t.submit}
                </button>
                <Link to="/contact" className="help-link">
                  {t.helpLink}
                </Link>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Why Book With Us */}
      <section className="why-book-section">
        <div className="container">
          <h2 className="section-heading">{t.whyBook}</h2>
          <div className="benefits-grid">
            {t.benefits.map((b, i) => (
              <motion.div
                key={i}
                className="benefit-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                {i === 0 && <Clock size={40} color="#C49818" />}
                {i === 1 && <Shield size={40} color="#C49818" />}
                {i === 2 && <Headphones size={40} color="#C49818" />}
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="booking-faq-section">
        <div className="container">
          <h2 className="section-heading">{t.faqHeading}</h2>
          <div className="faq-list">
            {t.faq.map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Booking;
